import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { requestResetStats, requestSurrender, forceUpdate, setRestart } from '../../HOME/REDUX/actions';
import { delay, playSound, windowSize, dbget, dbset } from '../../HOME/utils';
import { SWAP_DURATION } from '../const';
import * as f from '../logic';
import { setBlock, setOver, setCells, updateStats, updateSteps } from '../REDUX/actions';
import { setSteps, setInit, setSwapDuration } from '../REDUX/actions';
import '../styles.css';
import Cell from './Cell';
import MessageBox from '../../HOME/COMPONENTS/MessageBox';
import _ from 'lodash';

const Board = props => {
  const l = useRef(null);
  const [selectedCell, selectCell] = useState(0);
  const { rows, cols, cellCount } = f.sizeInfo(props.size);
  const { restart, size, forceUpdate } = props;
  const [progress, setProgress] = useState(false);
  const progressBox = useRef(null);
  const { setInit, setRestart, requestResetStats, requestSurrender } = props;

  useEffect(() => {
    window.addEventListener('resize', forceUpdate);

    if (!l.current) {
      load();
    } else if (restart) {
      setInit(false);

      init();
      setRestart(false);
    }

    return () => {
      window.removeEventListener('resize', forceUpdate);
      requestResetStats(false);
      requestSurrender(false);
    };
  }, [forceUpdate, setInit, setRestart, requestResetStats, requestSurrender, restart]);

  const load = () => {
    l.current = {};

    dbget(f.boardKey(props), json => {
      if (!json) {
        init();
        return;
      }

      const { steps, cells } = fromJson(json);

      f.validateCells(props, updateCellsByDest(cells), false);

      props.setCells(cells);
      props.setSteps(steps);

      setProgress(true);
      delay(() => progressBox.current && progressBox.current.click(), 2000);
    });
  };

  const handleDismissProgress = () => {
    delay(() => setProgress(false));
  };

  const palette = [
    [[2, 2], [2, 3], [3, 2]],
    [[2, 2], [2, 3], [3, 2]],
    [[2, 3], [3, 2], [3, 3]],
    [[3, 3], [3, 4], [4, 3]],
    [[3, 4], [4, 3], [4, 4]],
  ][props.size - 1];

  const init = () => {
    playSound(props, 'dice');

    const minShapes = _.minBy(palette, sc => sc[0])[0];
    const minColors = _.minBy(palette, sc => sc[1])[1];
    const sc = _.sample(palette);
    const shapes = _.sampleSize(['square', 'rhombus', 'star', 'circle', 'plus'], sc[0]);
    const colors = _.sampleSize(['red', 'yellow', 'green', 'blue', 'magenta'], sc[1]);

    let cells;

    while (true) {
      cells = [];

      for (let row = 1; row <= rows; row++) {
        for (let col = 1; col <= cols; col++) {
          const cell = { row, col };
          const left = f.getAdjacent(cells, row, col, 0, size);
          const above = f.getAdjacent(cells, row, col, 1, size);

          while (true) {
            cell.color = _.sample(colors);
            cell.shape = _.sample(shapes);

            if (f.isValidPair(cell, left) && f.isValidPair(cell, above)) {
              break;
            }
          }

          cells.push(cell);
        }
      }

      let count = _.uniqBy(cells, c => c.shape).length;

      if (count < minShapes) {
        continue;
      }

      count = _.uniqBy(cells, c => c.color).length;

      if (count < minColors) {
        continue;
      }

      break;
    }

    const keys = [...Array(cellCount).keys()];

    while (true) {
      for (let i = 0; i < keys.length; i++) {
        const indexes = _.sampleSize(keys, 2);
        const cell1 = cells[indexes[0]];
        const cell2 = cells[indexes[1]];

        if (!f.sameCell(cell1, cell2)) {
          const dest = f.getDest(cell1);
          cell1.dest = f.getDest(cell2);
          cell2.dest = dest;
        }
      }

      const over = f.validateCells(props, updateCellsByDest(cells), false);

      if (!over) {
        break;
      }
    }

    props.setCells(cells);
    props.setSteps(0);

    save(0, cells);
  };

  const updateCellsByDest = cells => {
    cells = _.sortBy(cells || props.cells, c => {
      const { row, col } = c.dest || c;
      return (row - 1) * cols + col - 1;
    });

    return cells;
  };

  const handleSelect = cell => {
    selectCell(cell);
  };

  const handleSwap = cell => {
    cell.swapping = selectedCell.swapping = true;

    const dest = f.getDest(cell);
    cell.dest = f.getDest(selectedCell);
    selectedCell.dest = dest;

    props.setBlock(true);
    selectCell(null);

    const steps = (props.steps || 0) + 1;
    props.setSteps(steps);

    const cells = updateCellsByDest();

    delay(() => {
      playSound(props, 'cluck');

      cell.swapping = selectedCell.swapping = false;
      const over = f.validateCells(props, cells);

      if (over) {
        f.setStats({ ...props, steps, size });
      } else {
        props.setBlock(false);
        save(steps, props.cells);
      }
    }, SWAP_DURATION);
  };

  const save = (steps, cells) => {
    dbset(f.boardKey(props), toJson(steps, cells));
  };

  const toJson = (steps, cells) => {
    const json = JSON.stringify({ steps, cells }, ['steps', 'cells', 'row', 'col', 'color', 'shape', 'dest']);

    return json;
  };

  const fromJson = json => {
    return JSON.parse(json);
  };

  if (!l.current) {
    return null;
  }

  const { surrender, resetStats, cells } = props;
  let { x, y } = windowSize();
  y -= 200;
  let width = Math.min(x, y) * 0.9;
  width = Math.min(width, 600);
  const cellWidth = (width / (x > y ? rows : cols)) * 0.95;
  const grid = `${_.repeat('auto ', rows)}/${_.repeat('auto ', cols)}`;

  const handleConfirmSurrender = yes => {
    delay(() => {
      requestSurrender(false);

      if (!yes) {
        return;
      }

      playSound(props, 'click');

      selectCell(null);
      props.setBlock(true);
      props.setSwapDuration(2000);

      f.setStats(props, true);

      delay(() => {
        playSound(props, 'cluck');
        props.setSwapDuration(SWAP_DURATION);
      }, 2000);

      _.each(cells, cell => {
        cell.dest = { row: cell.row, col: cell.col };
      });

      forceUpdate();

      delay(() => {
        f.validateCells(props, cells);
      }, 2500);
    });
  };

  const handleConfirmResetStats = yes => {
    if (yes) {
      props.updateStats();
    }

    delay(() => requestResetStats(false));
  };

  return (
    <div className="tt-board-container">
      <div className="tt-board" style={{ grid }}>
        {_.map(cells, (cell, i) => {
          return (
            <Cell key={i} cell={cell} width={cellWidth} selectedCell={selectedCell} select={c => handleSelect(c)} swap={c => handleSwap(c)} />
          );
        })}
      </div>
      {surrender ? <MessageBox message="Give up?" yesNo width={width} onReply={handleConfirmSurrender} /> : null}
      {resetStats ? <MessageBox message="Reset stats?" yesNo width={width} onReply={handleConfirmResetStats} /> : null}
      {progress ? <MessageBox ref={progressBox} message="GAME IN PROGRESS" onReply={handleDismissProgress} /> : null}
    </div>
  );
};

function mapStateToProps({ sm, common }) {
  return { ...sm, ...common };
}

export default connect(
  mapStateToProps,
  {
    updateStats,
    updateSteps,
    requestSurrender,
    requestResetStats,
    setCells,
    setBlock,
    setOver,
    setRestart,
    setSteps,
    setInit,
    setSwapDuration,
    forceUpdate,
  }
)(Board);
